import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { getGamificationStats, getAthleteBadges, getRecentXpEvents, awardBadge, setSelectedBadge, getFollowCounts, updateProfile, deleteMyAccount } from '../lib/database';
import { evaluateNewBadges, getBadgeByKey, BADGES, BADGE_CATEGORIES, getBadgesByCategory } from '../lib/badgeDefinitions';
import { totalXpForLevel, xpProgressInLevel } from '../lib/xpUtils';
import { getAlertPrefs, setAlertPrefs, unlockAudioAlert, playRestFinishedAlert, vibrationSupported } from '../lib/restAlert';
import { getPushStatus, enablePush, disablePush } from '../lib/restPush';
import BottomNav from './BottomNav';
import Avatar from './Avatar';
import EditProfileSheet from './EditProfileSheet';
import BadgeCategoryRow from './badges/BadgeCategoryRow';
import BadgeDetailSheet from './badges/BadgeDetailSheet';
import CategoryAllSheet from './badges/CategoryAllSheet';

function ProfilePage() {
  const { profile, setProfile, signOut, canCoach } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [coachMode, setCoachMode] = useState(!!profile?.coach_mode);
  const [coachBusy, setCoachBusy] = useState(false);

  // Elimina account
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirm.trim().toUpperCase() !== 'ELIMINA') return;
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteMyAccount();
      await signOut();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err.message || 'Errore durante l\'eliminazione. Riprova.');
      setDeleting(false);
    }
  };

  const toggleCoachMode = async () => {
    const next = !coachMode;
    setCoachMode(next);          // ottimistico
    setCoachBusy(true);
    try {
      await updateProfile(profile.id, { coach_mode: next });
      setProfile(prev => ({ ...prev, coach_mode: next })); // aggiorna canCoach globale
    } catch (e) {
      setCoachMode(!next);       // rollback
      toast.error('Impossibile aggiornare la Modalità Coach. Controlla la connessione e riprova.');
      console.error('Coach mode toggle failed:', e.message);
    } finally {
      setCoachBusy(false);
    }
  };
  const [selectedBadgeLocal, setSelectedBadgeLocal] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [detailBadge, setDetailBadge] = useState(null);   // badge aperto nel dettaglio
  const [allCategory, setAllCategory] = useState(null);   // categoria aperta in "Vedi tutto"
  const [editingProfile, setEditingProfile] = useState(false);
  const [alertPrefs, setAlertPrefsState] = useState(getAlertPrefs);
  const [pushStatus, setPushStatus] = useState(getPushStatus);
  const [pushBusy, setPushBusy] = useState(false);

  const toggleAlertPref = (key) => {
    unlockAudioAlert(); // tap = gesto → sblocca anche l'audio per il test
    const next = setAlertPrefs({ [key]: !alertPrefs[key] });
    setAlertPrefsState(next);
  };

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await enablePush(); // richiesta permesso: deve partire da questo tap
    } catch (e) {
      console.warn('Attivazione notifiche fallita:', e.message);
    } finally {
      setPushBusy(false);
      setPushStatus(getPushStatus());
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    try { await disablePush(); } finally {
      setPushBusy(false);
      setPushStatus(getPushStatus());
    }
  };

  const fetchProfileData = useCallback(async () => {
    if (!profile?.id) return null;

    const [stats, earnedBadges, recentXp, followCounts] = await Promise.all([
      getGamificationStats(profile.id),
      getAthleteBadges(profile.id),
      getRecentXpEvents(profile.id, 8),
      getFollowCounts(profile.id),
    ]);

    // Sync newly earned badges (non-blocking)
    const newBadges = evaluateNewBadges(stats, earnedBadges.map((b) => b.badge_key));
    if (newBadges.length > 0) {
      setSyncing(true);
      await Promise.allSettled(newBadges.map((b) => awardBadge(profile.id, b.key)));
      setSyncing(false);
      // Re-add to local list for immediate display
      newBadges.forEach((b) => earnedBadges.push({ badge_key: b.key, earned_at: new Date().toISOString() }));
    }

    return { stats, earnedBadges, recentXp, followCounts };
  }, [profile?.id]);

  const { data, loading } = useDataCache(
    profile?.id ? `athlete-profile-${profile.id}` : null,
    fetchProfileData,
    60000
  );

  // Initialize local selected badge from loaded data
  const stats = data?.stats;
  const earnedBadges = data?.earnedBadges || [];
  const recentXp = data?.recentXp || [];
  const followCounts = data?.followCounts || { followers: 0, following: 0 };
  const earnedKeys = new Set(earnedBadges.map((b) => b.badge_key));
  const earnedMap = new Map(earnedBadges.map((b) => [b.badge_key, b.earned_at]));
  const currentSelectedBadge = selectedBadgeLocal ?? stats?.selectedBadge ?? null;

  const level = stats?.currentLevel || 1;
  const totalXp = stats?.totalXp || 0;
  const progress = xpProgressInLevel(totalXp);
  const xpInLevel = totalXp - totalXpForLevel(level);
  const xpNeeded = totalXpForLevel(level + 1) - totalXpForLevel(level);

  const handleSelectBadge = async (badgeKey) => {
    const newKey = currentSelectedBadge === badgeKey ? null : badgeKey;
    setSelectedBadgeLocal(newKey);
    try {
      await setSelectedBadge(profile.id, newKey);
    } catch (e) {
      console.warn('[Profile] Could not save selected badge:', e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 page-with-bottom-nav flex items-center justify-center">
        <div className="text-gray-400 text-sm">Caricamento profilo...</div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 page-with-bottom-nav">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-5 pt-12 pb-5">
        <div className="flex items-center gap-4">
          <Avatar name={profile?.name} seed={profile?.id} src={profile?.avatar_url} size="w-14 h-14" textClass="text-xl" />
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-gray-900 truncate">{profile?.name || 'Atleta'}</h1>
            {profile?.username && (
              <p className="text-xs text-gray-400 truncate">@{profile.username}</p>
            )}
            {currentSelectedBadge && getBadgeByKey(currentSelectedBadge) && (
              <p className="text-sm text-primary-600 font-semibold flex items-center gap-1 mt-0.5">
                <span>{getBadgeByKey(currentSelectedBadge).icon}</span>
                <span>{getBadgeByKey(currentSelectedBadge).name}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => setEditingProfile(true)}
            className="shrink-0 text-xs font-semibold text-primary-600 border border-primary-200 rounded-full px-3 py-1.5 active:bg-primary-50"
          >
            Modifica
          </button>
        </div>
        {profile?.bio && (
          <p className="text-sm text-gray-600 mt-3 whitespace-pre-line">{profile.bio}</p>
        )}
        <div className="flex gap-6 mt-4">
          <div>
            <span className="text-base font-bold text-gray-900">{followCounts.followers}</span>
            <span className="text-sm text-gray-500 ml-1">follower</span>
          </div>
          <div>
            <span className="text-base font-bold text-gray-900">{followCounts.following}</span>
            <span className="text-sm text-gray-500 ml-1">following</span>
          </div>
        </div>
        {syncing && <p className="mt-2 text-xs text-gray-400">Sincronizzando…</p>}
      </header>

      <main className="px-4 py-5 space-y-6 max-w-lg mx-auto">

        {/* ── Level card ─────────────────────────────── */}
        <section className="bg-white rounded-2xl p-5 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white text-lg font-bold shrink-0">
                {level}
              </div>
              <div>
                <p className="font-bold text-gray-900">Livello {level}</p>
                <p className="text-xs text-gray-500">{totalXp} XP totali</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-400">Prossimo livello</p>
              <p className="text-sm font-semibold text-gray-700">{xpToNext(totalXp)} XP</p>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-xs text-gray-400 mb-1.5">
              <span>{xpInLevel} XP</span>
              <span>{xpNeeded} XP</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ width: `${(progress * 100).toFixed(1)}%` }}
              />
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3 pt-1">
            <StatPill label="Allenamenti" value={stats?.totalWorkouts ?? 0} />
            <StatPill label="Serie extra" value={stats?.extraSets ?? 0} />
            <StatPill label="Liberi" value={stats?.freeWorkouts ?? 0} />
          </div>
        </section>

        {/* ── Obiettivi e Badge ──────────────────────────
            Ogni card è una sfida: completala e sblocchi il badge/titolo. */}
        <section className="space-y-4">
          <div className="px-1">
            <h2 className="text-base font-bold text-gray-800">
              Obiettivi e Badge{' '}
              <span className="text-sm font-normal text-gray-400">
                ({earnedKeys.size}/{BADGES.length})
              </span>
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Completa la sfida per sbloccare il badge e usarlo come titolo. Tocca per i dettagli.
            </p>
          </div>

          {BADGE_CATEGORIES.map((cat) => (
            <BadgeCategoryRow
              key={cat.key}
              label={cat.label}
              badges={getBadgesByCategory(cat.key)}
              stats={stats}
              earnedMap={earnedMap}
              selectedKey={currentSelectedBadge}
              onBadgeClick={setDetailBadge}
              onSeeAll={() => setAllCategory(cat.key)}
            />
          ))}
        </section>

        {/* ── Storico XP ─────────────────────────────── */}
        {recentXp.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-base font-bold text-gray-800 px-1">Storico XP</h2>
            <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-50">
              {recentXp.map((event, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">⭐</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Allenamento completato</p>
                      <p className="text-xs text-gray-400">{formatDate(event.created_at)}</p>
                    </div>
                  </div>
                  <span className="text-sm font-bold text-yellow-600">+{event.xp_amount} XP</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── Modalità Coach / Personal Trainer ──────── */}
        <section className="space-y-3">
          <h2 className="text-base font-bold text-gray-800 px-1">Modalità Coach</h2>
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg">💪</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Attiva modalità Coach</p>
                  <p className="text-xs text-gray-400">
                    {coachMode ? 'Attiva — segui atleti e crea schede' : 'Per personal trainer: segui atleti e assegna schede'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={toggleCoachMode}
                disabled={coachBusy}
                className={`relative w-12 h-7 rounded-full transition-colors flex-none disabled:opacity-50 ${coachMode ? 'bg-primary-500' : 'bg-gray-200'}`}
                aria-pressed={coachMode}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${coachMode ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>
            {coachMode && (
              <p className="text-[11px] text-gray-400 mt-3 leading-snug">
                Trovi le funzioni coach nella voce <strong>Coach</strong> in basso: atleti, programmi e dashboard.
              </p>
            )}
          </div>
        </section>

        {/* ── Notifiche fine recupero ────────────────── */}
        <section className="space-y-3">
          <h2 className="text-base font-bold text-gray-800 px-1">Notifiche fine recupero</h2>
          <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-50">
            {/* Web Push (funziona anche in background/schermo bloccato) */}
            <div className="px-4 py-3">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-lg">📲</span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-800">Notifica push</p>
                    <p className="text-xs text-gray-400">
                      {pushStatus === 'enabled' && 'Attive ✓ — arrivano anche con app chiusa o schermo bloccato'}
                      {pushStatus === 'off' && 'Avvisa allo scadere del recupero anche in background'}
                      {pushStatus === 'denied' && 'Permesso negato'}
                      {pushStatus === 'need-install' && 'Serve l’app installata sulla schermata Home'}
                      {pushStatus === 'unsupported' && 'Non supportate da questo browser'}
                    </p>
                  </div>
                </div>
                {pushStatus === 'enabled' ? (
                  <button
                    type="button"
                    onClick={handleDisablePush}
                    disabled={pushBusy}
                    className="flex-none px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-xs font-semibold text-gray-600 transition-colors disabled:opacity-50"
                  >
                    Disattiva
                  </button>
                ) : pushStatus === 'off' ? (
                  <button
                    type="button"
                    onClick={handleEnablePush}
                    disabled={pushBusy}
                    className="flex-none px-3 py-2 rounded-lg gradient-primary text-white text-xs font-semibold shadow transition-all active:scale-98 disabled:opacity-50"
                  >
                    {pushBusy ? 'Attivo…' : 'Attiva notifiche'}
                  </button>
                ) : null}
              </div>
              {pushStatus === 'denied' && (
                <p className="text-[11px] text-amber-600 mt-2 leading-snug">
                  Hai negato il permesso: riattivalo da Impostazioni iPhone → Notifiche → Uptra,
                  poi torna qui e premi di nuovo Attiva.
                </p>
              )}
              {pushStatus === 'need-install' && (
                <p className="text-[11px] text-amber-600 mt-2 leading-snug">
                  Apri Uptra in Safari → Condividi → "Aggiungi alla schermata Home", poi apri
                  l'app dalla Home e attiva le notifiche da qui.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg">🔔</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Suono</p>
                  <p className="text-xs text-gray-400">Beep quando il recupero finisce</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleAlertPref('sound')}
                className={`relative w-12 h-7 rounded-full transition-colors flex-none ${alertPrefs.sound ? 'bg-primary-500' : 'bg-gray-200'}`}
                aria-pressed={alertPrefs.sound}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${alertPrefs.sound ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>

            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-3 min-w-0">
                <span className="text-lg">📳</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">Vibrazione</p>
                  <p className="text-xs text-gray-400">
                    {vibrationSupported() ? 'Vibra quando il recupero finisce' : 'Non supportata da questo dispositivo (iPhone)'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggleAlertPref('vibration')}
                disabled={!vibrationSupported()}
                className={`relative w-12 h-7 rounded-full transition-colors flex-none ${!vibrationSupported() ? 'bg-gray-100 opacity-50' : alertPrefs.vibration ? 'bg-primary-500' : 'bg-gray-200'}`}
                aria-pressed={alertPrefs.vibration}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${vibrationSupported() && alertPrefs.vibration ? 'left-[22px]' : 'left-0.5'}`} />
              </button>
            </div>

            <div className="px-4 py-3">
              <button
                type="button"
                onClick={() => { unlockAudioAlert(); playRestFinishedAlert(); }}
                className="w-full py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700 transition-colors"
              >
                ▶︎ Prova avviso
              </button>
              <p className="text-[11px] text-gray-400 mt-2 leading-snug">
                Suono e vibrazione avvisano quando l'app è aperta. Con app in background
                o schermo bloccato ci pensa la notifica push (attivala qui sopra).
              </p>
            </div>
          </div>
        </section>

        {/* ── Elimina account (zona pericolosa) ──────── */}
        <section className="space-y-2 pt-2">
          <h2 className="text-base font-bold text-gray-800 px-1">Elimina account</h2>
          <div className="bg-white rounded-2xl p-4 border border-red-100">
            <p className="text-sm text-gray-600 mb-3">
              L'eliminazione dell'account è <strong>definitiva e irreversibile</strong>.
            </p>
            <button
              type="button"
              onClick={() => { setShowDelete(true); setDeleteConfirm(''); setDeleteError(''); }}
              className="text-sm font-semibold text-red-600 border border-red-200 rounded-xl px-4 py-2 active:bg-red-50"
            >
              Elimina il mio account
            </button>
          </div>
        </section>

        {/* Marcatore build: permette di verificare quale commit è live */}
        <p className="text-center text-[10px] text-gray-300 pt-2">
          build {(process.env.REACT_APP_COMMIT || 'dev').slice(0, 7)}
        </p>

      </main>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-0 md:p-4" onClick={() => !deleting && setShowDelete(false)}>
          <div className="bg-white w-full md:max-w-md rounded-t-2xl md:rounded-2xl p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-extrabold text-gray-900 mb-2">Eliminare l'account?</h3>
            <p className="text-sm text-gray-600">
              Questa operazione è <strong>definitiva e irreversibile</strong>. Verranno eliminati il tuo profilo, gli allenamenti,
              i progressi, i badge, i follower e le notifiche.
            </p>
            {canCoach && (
              <p className="text-sm text-gray-600 mt-2">
                Le schede che hai creato e assegnato <strong>resteranno disponibili ai tuoi atleti</strong>; verrai rimosso come autore.
              </p>
            )}
            <p className="text-xs text-gray-400 mt-2">
              Eventuali copie nei backup tecnici possono persistere fino alla loro naturale scadenza.
            </p>
            <label className="block text-sm text-gray-700 mt-4 mb-1">Per confermare, digita <strong>ELIMINA</strong>:</label>
            <input
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="ELIMINA"
              className="w-full border border-gray-300 rounded-xl px-3 py-2 text-base focus:outline-none focus:border-red-400"
            />
            {deleteError && <div className="text-sm text-red-600 mt-2">{deleteError}</div>}
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                disabled={deleting}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 font-semibold text-gray-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'ELIMINA'}
                className="flex-1 py-2.5 rounded-xl font-semibold text-white bg-red-600 disabled:bg-red-300"
              >
                {deleting ? 'Eliminazione…' : 'Elimina definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />

      {/* Modifica profilo */}
      {editingProfile && (
        <EditProfileSheet
          activeTitleKey={currentSelectedBadge}
          onClose={() => setEditingProfile(false)}
        />
      )}

      {/* "Vedi tutto" di una categoria */}
      {allCategory && (
        <CategoryAllSheet
          label={BADGE_CATEGORIES.find((c) => c.key === allCategory)?.label || ''}
          badges={getBadgesByCategory(allCategory)}
          stats={stats}
          earnedMap={earnedMap}
          selectedKey={currentSelectedBadge}
          onBadgeClick={setDetailBadge}
          onClose={() => setAllCategory(null)}
        />
      )}

      {/* Dettaglio badge (stack sopra "Vedi tutto") */}
      {detailBadge && (
        <BadgeDetailSheet
          badge={detailBadge}
          earned={earnedKeys.has(detailBadge.key)}
          earnedAt={earnedMap.get(detailBadge.key)}
          selected={currentSelectedBadge === detailBadge.key}
          progress={{
            current: detailBadge.getProgress ? detailBadge.getProgress(stats || {}) : 0,
            target: detailBadge.target || 0,
          }}
          onClose={() => setDetailBadge(null)}
          onSetTitle={handleSelectBadge}
        />
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function StatPill({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-2.5 text-center">
      <p className="text-lg font-bold text-gray-900">{value}</p>
      <p className="text-xs text-gray-400 leading-tight mt-0.5">{label}</p>
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────

function xpToNext(totalXp) {
  const level = Math.max(1, (() => {
    let l = 1;
    while (totalXpForLevel(l + 1) <= totalXp) l++;
    return l;
  })());
  return totalXpForLevel(level + 1) - totalXp;
}

function formatDate(iso) {
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
    }).format(new Date(iso));
  } catch {
    return '';
  }
}

export default ProfilePage;
